import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { shiftRequestService, shiftService } from '../../services';
import { ShiftRequest, Shift } from '../../types';
import { ShiftRequestCard } from '../../components/ShiftRequestCard';
import { REQUEST_TYPES, REQUEST_STATUSES, REQUEST_TYPE_LABELS, REQUEST_STATUS_LABELS, DEFAULT_PAGE_SIZE } from '../../constants';
import { usePagination } from '../../hooks';
import { toast } from '../../components/Toast';
import styles from './ShiftRequests.module.css';

export const ShiftRequests: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<ShiftRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [availableShifts, setAvailableShifts] = useState<Shift[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(false);

  // Use pagination custom hook
  const { page, totalPages, setTotalPages, resetPage, nextPage, previousPage, isFirstPage, isLastPage, hasMultiplePages } = usePagination();

  // Filtri
  const [statusFilter, setStatusFilter] = useState<number | ''>('');
  const [typeFilter, setTypeFilter] = useState<number | ''>('');

  // Form state
  const [formData, setFormData] = useState({
    requestType: REQUEST_TYPES.LEAVE,
    startDate: '',
    endDate: '',
    reason: '',
    targetShiftId: ''
  });

  // Memoize canReview to avoid recalculation
  const canReview = useMemo(() => {
    console.log('[ShiftRequests] User:', user);
    console.log('[ShiftRequests] User Role:', user?.role);
    console.log('[ShiftRequests] User Role Type:', typeof user?.role);
    console.log('[ShiftRequests] canReview:', user && user.role <= 2);
    return user && user.role <= 2;
  }, [user]);

  // Memoize loadRequests to prevent recreation on every render
  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const filter: {
        page: number;
        pageSize: number;
        status?: number;
        requestType?: number;
        employeeId?: number;
      } = { page, pageSize: DEFAULT_PAGE_SIZE.REQUESTS };

      if (statusFilter !== '') {
        filter.status = statusFilter;
      }

      if (typeFilter !== '') {
        filter.requestType = typeFilter;
      }

      // Employee vede solo le proprie richieste
      if (user?.role === 3 && user.id) {
        filter.employeeId = user.id;
      }

      const response = await shiftRequestService.getShiftRequests(filter);
      setRequests(response.data);
      setTotalPages(response.totalPages);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento delle richieste';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, user?.role, user?.id, setTotalPages]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Carica i turni disponibili quando il form viene aperto e il tipo è SwapShift
  const loadAvailableShifts = useCallback(async () => {
    if (!user?.id) return;

    setLoadingShifts(true);
    try {
      // Carica i turni futuri dell'utente
      const today = new Date().toISOString().split('T')[0];
      const response = await shiftService.getShifts({
        page: 1,
        pageSize: 100, // Carica molti turni per avere più opzioni
        employeeId: user.id,
        status: 0, // Solo turni schedulati
      });

      // Filtra solo i turni futuri
      const futureShifts = response.data.filter((shift: Shift) => {
        const shiftDate = new Date(shift.startTime).toISOString().split('T')[0];
        return shiftDate >= today;
      });

      setAvailableShifts(futureShifts);
    } catch (err: unknown) {
      console.error('Errore nel caricamento dei turni:', err);
    } finally {
      setLoadingShifts(false);
    }
  }, [user?.id]);

  // Carica i turni quando viene selezionato il tipo "Scambio Turno"
  useEffect(() => {
    if (showCreateForm && parseInt(formData.requestType.toString()) === REQUEST_TYPES.SWAP_SHIFT) {
      loadAvailableShifts();
    }
  }, [showCreateForm, formData.requestType, loadAvailableShifts]);

  // Memoize form reset data
  const initialFormData = useMemo(
    () => ({
      requestType: REQUEST_TYPES.LEAVE,
      startDate: '',
      endDate: '',
      reason: '',
      targetShiftId: '',
    }),
    []
  );

  // Memoize handleCreateRequest to prevent recreation
  const handleCreateRequest = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      try {
        const payload: {
          type: number;
          requestedDateStart: string;
          requestedDateEnd?: string;
          reason?: string;
          shiftId?: number;
        } = {
          type: parseInt(formData.requestType.toString()),
          requestedDateStart: formData.startDate,
          requestedDateEnd: formData.endDate,
          reason: formData.reason || undefined,
        };

        // Aggiungi shiftId solo se il tipo è SwapShift
        if (parseInt(formData.requestType.toString()) === REQUEST_TYPES.SWAP_SHIFT && formData.targetShiftId) {
          payload.shiftId = parseInt(formData.targetShiftId);
        }

        await shiftRequestService.createShiftRequest(payload);
        toast.success('Richiesta creata con successo!');
        setShowCreateForm(false);
        setFormData(initialFormData);
        loadRequests();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nella creazione della richiesta';
        setError(message);
      }
    },
    [formData, initialFormData, loadRequests]
  );

  // Memoize handleApprove to prevent recreation
  const handleApprove = useCallback(
    async (id: number) => {
      const notes = prompt('Note (opzionale):');
      if (notes === null) return; // User cancelled

      try {
        await shiftRequestService.reviewShiftRequest(id, {
          approved: true,
          rejectionReason: notes || undefined,
        });
        toast.success('Richiesta approvata con successo!');
        loadRequests();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Errore nell'approvazione della richiesta";
        toast.error(message);
      }
    },
    [loadRequests]
  );

  // Memoize handleReject to prevent recreation
  const handleReject = useCallback(
    async (id: number) => {
      const notes = prompt('Motivazione del rifiuto (opzionale):');
      if (notes === null) return; // User cancelled

      try {
        await shiftRequestService.reviewShiftRequest(id, {
          approved: false,
          rejectionReason: notes || undefined,
        });
        toast.success('Richiesta rifiutata');
        loadRequests();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nel rifiuto della richiesta';
        toast.error(message);
      }
    },
    [loadRequests]
  );

  // Memoize handleCancel to prevent recreation
  const handleCancel = useCallback(
    async (id: number) => {
      if (!window.confirm('Sei sicuro di voler cancellare questa richiesta?')) {
        return;
      }

      try {
        await shiftRequestService.cancelShiftRequest(id);
        toast.success('Richiesta cancellata con successo');
        loadRequests();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Errore nella cancellazione della richiesta';
        toast.error(message);
      }
    },
    [loadRequests]
  );

  // Memoize form field change handlers
  const handleFormChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  // Memoize filter change handlers
  const handleStatusFilterChange = useCallback(
    (value: string) => {
      setStatusFilter(value === '' ? '' : parseInt(value));
      resetPage();
    },
    [resetPage]
  );

  const handleTypeFilterChange = useCallback(
    (value: string) => {
      setTypeFilter(value === '' ? '' : parseInt(value));
      resetPage();
    },
    [resetPage]
  );

  return (
    <div className={styles.requests}>
      <div className={styles.header}>
        <h1>📋 Richieste Ferie/Scambi</h1>
        {user?.role === 3 && (
          <button
            className={styles.createButton}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '❌ Annulla' : '➕ Nuova Richiesta'}
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className={styles.formContainer}>
          <h2>Crea Nuova Richiesta</h2>
          <form onSubmit={handleCreateRequest} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="requestType">Tipo Richiesta:</label>
              <select
                id="requestType"
                value={formData.requestType}
                onChange={(e) =>
                  handleFormChange('requestType', parseInt(e.target.value))
                }
                className={styles.select}
                required
              >
                <option value={REQUEST_TYPES.LEAVE}>{REQUEST_TYPE_LABELS[REQUEST_TYPES.LEAVE]}</option>
                <option value={REQUEST_TYPES.SWAP_SHIFT}>{REQUEST_TYPE_LABELS[REQUEST_TYPES.SWAP_SHIFT]}</option>
                <option value={REQUEST_TYPES.EXTRA_SHIFT}>{REQUEST_TYPE_LABELS[REQUEST_TYPES.EXTRA_SHIFT]}</option>
                <option value={REQUEST_TYPES.ABSENCE}>{REQUEST_TYPE_LABELS[REQUEST_TYPES.ABSENCE]}</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="startDate">Data Inizio:</label>
              <input
                type="date"
                id="startDate"
                value={formData.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="endDate">Data Fine:</label>
              <input
                type="date"
                id="endDate"
                value={formData.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                className={styles.input}
                required
              />
            </div>

            {parseInt(formData.requestType.toString()) === REQUEST_TYPES.SWAP_SHIFT && (
              <div className={styles.formGroup}>
                <label htmlFor="targetShiftId">Seleziona Turno da Scambiare:</label>
                {loadingShifts ? (
                  <div className={styles.loading}>Caricamento turni...</div>
                ) : availableShifts.length === 0 ? (
                  <p className={styles.noShifts}>Nessun turno futuro disponibile per lo scambio</p>
                ) : (
                  <select
                    id="targetShiftId"
                    value={formData.targetShiftId}
                    onChange={(e) => handleFormChange('targetShiftId', e.target.value)}
                    className={styles.select}
                    required
                  >
                    <option value="">-- Seleziona un turno --</option>
                    {availableShifts.map((shift) => {
                      const startDate = new Date(shift.startTime);
                      const endDate = new Date(shift.endTime);
                      const dateStr = startDate.toLocaleDateString('it-IT', {
                        weekday: 'short',
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      });
                      const startTime = startDate.toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                      const endTime = endDate.toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit',
                      });

                      return (
                        <option key={shift.id} value={shift.id}>
                          {dateStr} • {startTime} - {endTime} • {shift.role || 'Nessun ruolo'}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="reason">Motivazione (opzionale):</label>
              <textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => handleFormChange('reason', e.target.value)}
                className={styles.textarea}
                rows={3}
                placeholder="Descrivi il motivo della richiesta..."
              />
            </div>

            <button type="submit" className={styles.submitButton}>
              ✅ Invia Richiesta
            </button>
          </form>
        </div>
      )}

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label htmlFor="status">Stato:</label>
          <select
            id="status"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutti</option>
            <option value={REQUEST_STATUSES.PENDING}>{REQUEST_STATUS_LABELS[REQUEST_STATUSES.PENDING]}</option>
            <option value={REQUEST_STATUSES.APPROVED}>{REQUEST_STATUS_LABELS[REQUEST_STATUSES.APPROVED]}</option>
            <option value={REQUEST_STATUSES.REJECTED}>{REQUEST_STATUS_LABELS[REQUEST_STATUSES.REJECTED]}</option>
            <option value={REQUEST_STATUSES.CANCELLED}>{REQUEST_STATUS_LABELS[REQUEST_STATUSES.CANCELLED]}</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="type">Tipo:</label>
          <select
            id="type"
            value={typeFilter}
            onChange={(e) => handleTypeFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutti</option>
            <option value={REQUEST_TYPES.LEAVE}>Ferie</option>
            <option value={REQUEST_TYPES.SWAP_SHIFT}>Scambio Turno</option>
            <option value={REQUEST_TYPES.EXTRA_SHIFT}>Turno Extra</option>
            <option value={REQUEST_TYPES.ABSENCE}>Assenza</option>
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento richieste...</div>
      ) : requests.length === 0 ? (
        <div className={styles.empty}>
          <p>📭 Nessuna richiesta trovata</p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {requests.map((request) => (
              <ShiftRequestCard
                key={request.id}
                request={request}
                onApprove={canReview ? handleApprove : undefined}
                onReject={canReview ? handleReject : undefined}
                onCancel={!canReview ? handleCancel : undefined}
                showActions={true}
                canReview={!!canReview}
              />
            ))}
          </div>

          {hasMultiplePages && (
            <div className={styles.pagination}>
              <button
                onClick={previousPage}
                disabled={isFirstPage}
                className={styles.pageButton}
              >
                ← Precedente
              </button>
              <span className={styles.pageInfo}>
                Pagina {page} di {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={isLastPage}
                className={styles.pageButton}
              >
                Successiva →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ShiftRequests.displayName = 'ShiftRequests';
